import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, UserPlus, Trash2, Mail, Loader2 } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { toast } from 'sonner';

export default function AdminMembersPage() {
  const { user } = useAuth();
  const isFounder = Boolean(user?.is_founder);

  const [data, setData] = useState({ members: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.admin.getMembers();
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar administradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.includes('@')) return;
    setInviting(true);
    try {
      const res = await api.admin.inviteAdmin(inviteEmail.trim().toLowerCase());
      toast.success(`Convite enviado para ${res.invite?.email}`);
      setInviteEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (member) => {
    if (!window.confirm(`Revogar acesso de ${member.email}? Esta ação pode ser desfeita enviando um novo convite.`)) return;
    try {
      await api.admin.revokeMember(member.id);
      toast.success(`Acesso revogado de ${member.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao revogar acesso');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold" data-testid="admin-members-title">Administradores</h1>
          <p className="text-gray-500">
            {isFounder
              ? 'Como founder, você pode convidar e revogar acesso de novos administradores.'
              : 'Apenas o admin principal (founder) pode convidar ou revogar administradores.'}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-amber-500" /> Administradores ativos</CardTitle>
            <CardDescription>Quem tem acesso ao painel hoje</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {data.members?.map((m) => (
                  <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-row-${m.email}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{m.full_name || m.email}</p>
                      <p className="text-xs text-slate-500 truncate">{m.email}</p>
                      {m.created_at && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Adicionado em {new Date(m.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {m.is_founder ? (
                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">Founder</Badge>
                      ) : (
                        <Badge variant="secondary">Admin convidado</Badge>
                      )}
                      {isFounder && !m.is_founder && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-200 hover:border-red-300"
                          onClick={() => handleRevoke(m)}
                          data-testid={`revoke-btn-${m.email}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Revogar
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {isFounder && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-amber-500" /> Convidar novo administrador</CardTitle>
              <CardDescription>Um e-mail será enviado com um link para o convidado definir a própria senha. Convite expira em 48h.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3" data-testid="admin-invite-form">
                <div className="flex-1">
                  <Label htmlFor="invite-email" className="sr-only">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="email@exemplo.com"
                      className="pl-9"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={inviting} className="bg-gradient-to-r from-amber-500 to-red-500 text-white" data-testid="invite-submit-btn">
                  {inviting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : <><UserPlus className="w-4 h-4 mr-2" /> Enviar convite</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {data.pending_invites?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Convites pendentes</CardTitle>
              <CardDescription>Aguardando aceite. Expiram em 48h.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {data.pending_invites.map((inv) => (
                  <li key={inv.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <p className="font-medium">{inv.email}</p>
                      <p className="text-xs text-slate-500">
                        Convidado por {inv.invited_by_email} · expira {new Date(inv.expires_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
